import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarX,
  CheckSquare,
  Loader2,
  MailPlus,
  Package2,
  PackageMinus,
  Square,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDate, formatMoney, formatPercent, formatPeriod } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";

const CATEGORY_META = {
  discrepancy: { label: "Soll/Ist-Abweichung", icon: AlertTriangle, color: "rose" },
  missing_customer: { label: "Fehlender Kunde", icon: UserX, color: "amber" },
  product_lost: { label: "Produkt fehlt", icon: PackageMinus, color: "amber" },
  date_mismatch: { label: "Start-Datum", icon: CalendarX, color: "amber" },
  no_products: { label: "Keine Produkte", icon: Package2, color: "slate" },
};

export default function Audit() {
  const [data, setData] = useState({ issues: [], total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: "", intro: "", recipient: "" });

  const reload = async () => {
    try {
      const r = await api.get("/audit/issues");
      setData(r.data);
      // remove selected ids that no longer exist
      setSelected((prev) => {
        const next = new Set();
        const ids = new Set(r.data.issues.map((i) => i.id));
        for (const v of prev) if (ids.has(v)) next.add(v);
        return next;
      });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return data.issues;
    return data.issues.filter((i) => i.category === filter);
  }, [data.issues, filter]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = filtered.every((i) => next.has(i.id));
      if (allSelected) {
        filtered.forEach((i) => next.delete(i.id));
      } else {
        filtered.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  const openClaim = () => {
    if (selected.size === 0) {
      toast.error("Bitte erst Einträge auswählen.");
      return;
    }
    const first = data.issues.find((i) => selected.has(i.id));
    setForm({
      subject: `Korrekturanfrage – ${selected.size} Punkte aus aktuellem Audit`,
      intro: "",
      recipient: "",
      related_report_id: first?.report_id,
    });
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/audit/claim-from-issues", {
        subject: form.subject,
        intro: form.intro || undefined,
        recipient_email: form.recipient || undefined,
        issue_ids: Array.from(selected),
        related_report_id: form.related_report_id,
      });
      toast.success(`Claim mit ${r.data.issue_count} Punkt(en) eröffnet`);
      setOpen(false);
      setSelected(new Set());
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="audit-page">
      <PageHeader
        title="Audit / Fehler"
        subtitle={`${data.total} Punkte erkannt`}
        actions={
          <Button
            onClick={openClaim}
            disabled={selected.size === 0}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="audit-claim-btn"
          >
            <MailPlus size={16} className="mr-2" />
            Aus {selected.size || 0} Punkt{selected.size === 1 ? "" : "en"} Claim erstellen
          </Button>
        }
      >
        Aggregierte Übersicht aller potenziellen Fehler. Wähle Punkte aus, die im nächsten Claim an
        Lightspeed gemeldet werden sollen.
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        {/* category counters */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <CategoryButton
            label="Alle"
            count={data.total}
            active={filter === "all"}
            onClick={() => setFilter("all")}
            testid="audit-filter-all"
          />
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <CategoryButton
              key={key}
              icon={meta.icon}
              label={meta.label}
              count={data.by_category?.[key] || 0}
              active={filter === key}
              onClick={() => setFilter(key)}
              testid={`audit-filter-${key}`}
            />
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <button
              onClick={toggleAllVisible}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-900"
              data-testid="audit-toggle-all"
            >
              {filtered.length > 0 && filtered.every((i) => selected.has(i.id)) ? (
                <CheckSquare size={14} />
              ) : (
                <Square size={14} />
              )}
              Sichtbare auswählen
            </button>
            <div className="text-xs text-slate-500">
              {selected.size} von {data.total} ausgewählt
            </div>
          </div>
          {loading && (
            <div className="px-5 py-10 text-center text-slate-500">
              <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Lade Audit-Ergebnisse …
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-5 py-12 text-center text-slate-500">
              Keine offenen Punkte in dieser Kategorie.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {filtered.map((issue) => {
              const meta = CATEGORY_META[issue.category] || { label: issue.category, icon: AlertTriangle };
              const Icon = meta.icon;
              const isSelected = selected.has(issue.id);
              return (
                <li
                  key={issue.id}
                  className={`flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors ${
                    isSelected ? "bg-slate-50" : "hover:bg-slate-50/60"
                  }`}
                  onClick={() => toggle(issue.id)}
                  data-testid={`audit-issue-${issue.id}`}
                >
                  <button
                    className="mt-0.5 text-slate-500 hover:text-slate-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(issue.id);
                    }}
                  >
                    {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                  <Icon size={16} className="mt-0.5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-slate-900">{issue.title}</span>
                      <StatusBadge status={issue.severity === "high" ? "error" : issue.severity === "medium" ? "warning" : "default"} label={meta.label} />
                    </div>
                    <div className="text-sm text-slate-600">{issue.description}</div>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                      {issue.customer_id && (
                        <Link
                          to={`/customers/${issue.customer_id}`}
                          className="hover:text-slate-900 underline underline-offset-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          → Kunde
                        </Link>
                      )}
                      {issue.report_id && (
                        <Link
                          to={`/reports/${issue.report_id}`}
                          className="hover:text-slate-900 underline underline-offset-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          → Report {issue.report_po}
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="audit-claim-dialog">
          <DialogHeader>
            <DialogTitle>Claim aus {selected.size} Audit-Punkt{selected.size === 1 ? "" : "en"}</DialogTitle>
            <DialogDescription>
              Wir generieren eine E-Mail mit allen ausgewählten Punkten und eröffnen einen neuen
              Dialog. Der Status bleibt „offen", solange Du darauf antwortest.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Empfänger (optional)</Label>
              <Input
                value={form.recipient}
                onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                placeholder="karim.kessab@lightspeedhq.com"
                data-testid="audit-claim-recipient"
              />
            </div>
            <div className="space-y-1">
              <Label>Betreff</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="audit-claim-subject"
              />
            </div>
            <div className="space-y-1">
              <Label>Einleitung (optional)</Label>
              <Textarea
                rows={5}
                value={form.intro}
                onChange={(e) => setForm({ ...form, intro: e.target.value })}
                placeholder="Hallo Karim, …"
                data-testid="audit-claim-intro"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.subject.trim()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="audit-claim-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Senden &amp; Claim eröffnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryButton({ label, count, active, onClick, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-sm border transition-colors ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white hover:border-slate-400"
      }`}
    >
      <div className={`kbd-label ${active ? "text-slate-200" : ""} flex items-center gap-1.5`}>
        {Icon && <Icon size={12} />}
        {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1 tabular-nums">{count}</div>
    </button>
  );
}
